import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";
import { Copy, Share2, CheckCircle2, Lock, Unlock, Gift, ArrowRight, Crown } from "lucide-react";

export default function Invite() {
  const { user } = useAuth();
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [rewards, setRewards] = useState({ active_referrals: 0, tiers: [] });

  useEffect(() => {
    (async () => {
      try {
        const [l, r] = await Promise.all([
          api.get("/dashboard/referral-link"),
          api.get("/dashboard/rewards"),
        ]);
        setLink(`${window.location.origin}/registracija?ref=${l.data.code}`);
        setRewards(r.data);
      } catch {}
    })();
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Pozivni link kopiran!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Greška pri kopiranju");
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "NAŠA Myner Adria",
          text: "Pridruži se NAŠA Myner Adria zajednici!",
          url: link,
        });
      } catch {}
    } else copyLink();
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="invite-page">
      <div className="flex justify-end mb-4"><BackButton /></div>
      <div>
        <p className="text-xs uppercase tracking-[0.4em] text-zinc-500">Referral program</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-wide mt-1">
          Pozovi <span className="text-neon-orange">prijatelje</span>
        </h1>
      </div>

      {/* Referral link */}
      <div className="nasa-card p-6 md:p-8">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-3">
          <Share2 className="w-3 h-3 text-[#22ff77]" /> Vaš jedinstveni link
        </div>
        <h2 className="font-display text-2xl mb-4">
          Vaš kod: <span className="text-neon-green font-mono">{user?.referral_code}</span>
        </h2>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="invite-link-input"
            readOnly
            value={link}
            className="flex-1 bg-black border border-white/10 px-3 py-3 text-xs sm:text-sm font-mono text-[#22ff77] rounded-sm"
          />
          <button
            data-testid="invite-copy"
            onClick={copyLink}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#22ff77] text-black font-bold uppercase tracking-widest text-xs rounded-sm hover:brightness-110 glow-green"
          >
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Kopirano" : "Kopiraj"}
          </button>
          <button
            data-testid="invite-share"
            onClick={shareLink}
            className="flex items-center justify-center gap-2 px-4 py-3 border border-[#ff7a18]/60 text-[#ff7a18] font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-[#ff7a18]/10"
          >
            <Share2 className="w-4 h-4" />
            Podeli
          </button>
        </div>
      </div>

      {/* Reward tiers */}
      <div data-testid="rewards-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Nagrade za referale</div>
            <h2 className="font-display text-2xl font-bold mt-1">Stupnjevi <span className="text-neon-green">nagrada</span></h2>
          </div>
          <div className="text-xs text-zinc-500">
            Aktivnih: <span className="text-[#22ff77] font-bold text-lg">{rewards.active_referrals}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rewards.tiers.map((t) => {
            const pct = Math.min(100, (rewards.active_referrals / t.threshold) * 100);
            return (
              <div
                key={t.key}
                data-testid={`tier-${t.key}`}
                className={`nasa-card p-6 relative overflow-hidden ${t.unlocked ? "border-[#22ff77]/50 glow-green" : ""}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                      t.unlocked
                        ? "bg-[#22ff77]/15 border border-[#22ff77]/50 text-[#22ff77]"
                        : "bg-zinc-900 border border-white/10 text-zinc-500"
                    }`}>
                      {t.unlocked ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="font-display text-xs uppercase tracking-widest text-zinc-500">
                        {t.threshold} aktivnih prijatelja
                      </div>
                      <div className="font-display text-lg font-bold leading-tight">{t.title}</div>
                    </div>
                  </div>
                  <Gift className={`w-5 h-5 ${t.unlocked ? "text-[#22ff77]" : "text-zinc-700"}`} />
                </div>

                <p className="text-sm text-zinc-400 mb-4">{t.description}</p>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] uppercase tracking-widest">
                    <span className="text-zinc-500">Napredak</span>
                    <span className={t.unlocked ? "text-[#22ff77]" : "text-zinc-400"}>
                      {t.progress} / {t.threshold}
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: t.unlocked ? "#22ff77" : "#ff7a18",
                        boxShadow: t.unlocked ? "0 0 12px rgba(34,255,119,0.6)" : "none",
                      }}
                    />
                  </div>
                </div>

                {t.unlocked && (
                  <div className="mt-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#22ff77]">
                    Otključano <ArrowRight className="w-3 h-3" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* How it works */}
      <div className="nasa-card p-6 md:p-8">
        <h3 className="font-display text-lg mb-4">Kako funkcioniše</h3>
        <ol className="space-y-3 text-sm text-zinc-400">
          <li className="flex gap-3"><Step n={1} /> Podelite svoj pozivni link sa prijateljima.</li>
          <li className="flex gap-3"><Step n={2} /> Kada se prijatelj registruje, dobijate +1 NAŠA Token.</li>
          <li className="flex gap-3"><Step n={3} /> Kada prijatelj aktivira članstvo (prva kupovina tiketa), postaje aktivan referal.</li>
          <li className="flex gap-3"><Step n={4} /> Po dostizanju nivoa, automatski otključavate nagrade iznad.</li>
        </ol>
      </div>

      {/* Leaderboard CTA */}
      <Link
        to="/promoteri"
        data-testid="leaderboard-cta"
        className="block nasa-card p-6 md:p-8 hover:border-[#ff7a18]/50 transition-all group"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-[#ff7a18]/10 border border-[#ff7a18]/40 flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#ff7a18]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Javna lista</div>
              <h3 className="font-display text-xl font-bold">
                Najbolji <span className="text-neon-orange">promoteri</span> Adria regije
              </h3>
              <p className="text-xs text-zinc-400 mt-1">
                Top 3 postaju <span className="text-[#22ff77]">ambasadori projekta</span>
              </p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-[#ff7a18] group-hover:translate-x-1 transition-all" />
        </div>
      </Link>
    </div>
  );
}

function Step({ n }) {
  return (
    <span className="shrink-0 w-7 h-7 rounded-sm bg-[#22ff77]/10 border border-[#22ff77]/40 text-[#22ff77] font-display font-bold text-sm flex items-center justify-center">
      {n}
    </span>
  );
}
